"use client";

import Link from "@/components/locale-link";
import { DataTable, Notice } from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import type { CIPHER_IDENTIFIER_STRINGS } from "@/components/tools/cipher-identifier";
import { t } from "@/lib/i18n/interpolate";
import type {
  FamilyId,
  Finding,
  Identification,
} from "@/lib/tool-kit/ciphers-misc/cipher-identifier";

type Strings = typeof CIPHER_IDENTIFIER_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const FOUR_DIGITS: Intl.NumberFormatOptions = { maximumFractionDigits: 4 };
const TWO_DIGITS: Intl.NumberFormatOptions = { maximumFractionDigits: 2 };
const KEY_LENGTHS_SHOWN = 3;

/** Solver pages for each family; only tools that exist are listed. */
export const FAMILY_LINKS: Record<FamilyId, readonly string[]> = {
  english: ["letter-frequency-counter"],
  shift: ["caesar-cipher", "affine-cipher"],
  atbash: ["atbash-cipher", "affine-cipher"],
  affine: ["affine-cipher", "caesar-cipher"],
  substitution: ["substitution-cipher", "letter-frequency-counter"],
  transposition: [
    "rail-fence-cipher",
    "columnar-transposition-cipher",
    "scytale-cipher",
    "route-cipher",
  ],
  periodic: ["vigenere-cipher", "beaufort-cipher", "porta-cipher"],
  digraph: ["playfair-cipher", "four-square-cipher", "hill-cipher"],
  flat: [
    "autokey-cipher",
    "bifid-cipher",
    "trifid-cipher",
    "one-time-pad",
    "enigma-machine",
  ],
  adfgvx: ["adfgvx-cipher", "polybius-square-cipher"],
  bacon: ["bacon-cipher"],
  morse: ["morse-code-translator"],
  binary: ["binary-code-translator", "bacon-cipher"],
  a1z26: ["a1z26-cipher"],
  polybius: ["polybius-square-cipher"],
  book: ["book-cipher", "nihilist-cipher"],
  phone: ["phone-keypad-cipher"],
  hex: ["hex-converter", "xor-cipher", "ascii-converter"],
  "ascii-numbers": ["ascii-converter", "nihilist-cipher"],
  base32: ["base-encoding-converter", "text-encoder-decoder"],
  base64: ["text-encoder-decoder", "base-encoding-converter"],
  ascii85: ["base-encoding-converter"],
  braille: ["braille-translator"],
  symbols: ["pigpen-cipher", "braille-translator"],
  "other-script": [],
};

function reasonText(
  strings: Strings,
  key: string,
  params: Record<string, number | string> | undefined,
  fmt: Format
): string {
  const templates: Record<string, string> = strings.reasons;
  const filled: Record<string, string> = {};
  for (const [name, value] of Object.entries(params ?? {})) {
    filled[name] = typeof value === "number" ? fmt(value, FOUR_DIGITS) : value;
  }
  return t(templates[key] ?? key, filled);
}

function FindingCard({
  strings,
  finding,
  fmt,
}: {
  strings: Strings;
  finding: Finding;
  fmt: Format;
}) {
  const info = strings.families[finding.family];
  const links: Record<string, string> = strings.linkLabels;
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-foreground text-lg">{info.name}</h3>
        <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground text-xs">
          {strings.levels[finding.level]}
        </span>
      </div>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {info.summary}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
        {finding.reasons.map((reason) => (
          <li key={reason.key}>
            {reasonText(strings, reason.key, reason.params, fmt)}
          </li>
        ))}
      </ul>
      {FAMILY_LINKS[finding.family].length > 0 ? (
        <p className="text-sm">
          <span className="font-medium text-foreground">
            {strings.tryNext}{" "}
          </span>
          {FAMILY_LINKS[finding.family].map((slug, position) => (
            <span key={slug}>
              {position > 0 ? ", " : ""}
              <Link className="underline" href={`/tools/${slug}`}>
                {links[slug]}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
    </li>
  );
}

function statisticsRows(
  strings: Strings,
  result: Identification,
  fmt: Format
): string[][] {
  const { stats } = result;
  if (stats.letters === 0) {
    return [[strings.statCharacters, fmt(stats.characters), ""]];
  }
  const rows: string[][] = [
    [strings.statLetters, fmt(stats.letters), ""],
    [strings.statOther, fmt(stats.characters - stats.letters), ""],
    [strings.statDistinct, fmt(stats.distinctLetters), ""],
  ];
  if (stats.ic !== null) {
    rows.push([strings.statIc, fmt(stats.ic, FOUR_DIGITS), strings.statIcNote]);
  }
  if (stats.pairScore !== null) {
    rows.push([
      strings.statPairs,
      fmt(stats.pairScore, TWO_DIGITS),
      strings.statPairsNote,
    ]);
  }
  if (stats.chiPerLetter !== null) {
    rows.push([
      strings.statChi,
      fmt(stats.chiPerLetter, TWO_DIGITS),
      strings.statChiNote,
    ]);
  }
  if (stats.periods.length > 0) {
    rows.push([
      strings.statPeriods,
      stats.periods
        .slice(0, KEY_LENGTHS_SHOWN)
        .map((guess) =>
          t(strings.periodItem, {
            length: fmt(guess.length),
            ic: fmt(guess.ic, FOUR_DIGITS),
          })
        )
        .join("; "),
      strings.statPeriodsNote,
    ]);
  }
  if (stats.kasiski.length > 0) {
    rows.push([
      strings.statKasiski,
      stats.kasiski
        .map((entry) =>
          t(strings.kasiskiItem, {
            factor: fmt(entry.factor),
            count: fmt(entry.count),
          })
        )
        .join("; "),
      strings.statKasiskiNote,
    ]);
  }
  return rows;
}

export function IdentifierResults({
  strings,
  result,
  fmt,
}: {
  strings: Strings;
  result: Identification | null;
  fmt: Format;
}) {
  if (result === null) {
    return <Notice>{strings.empty}</Notice>;
  }
  return (
    <div className="space-y-4">
      {result.short ? <Notice>{strings.shortText}</Notice> : null}
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <p className={HINT}>{strings.resultsHint}</p>
      <ol className="space-y-3">
        {result.findings.map((finding) => (
          <FindingCard
            finding={finding}
            fmt={fmt}
            key={finding.family}
            strings={strings}
          />
        ))}
      </ol>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.statsHeading}</h2>
        <DataTable
          head={[strings.colMeasure, strings.colValue, strings.colMeaning]}
          rows={statisticsRows(strings, result, fmt)}
        />
      </section>
    </div>
  );
}
